// Tailwind brightness options mapped to slider steps
interface BrightnessOption {
    readonly class: string;
    readonly label: string;
}

interface SaturateOption {
    readonly value: number;
    readonly class: string;
    readonly label: string;
}

export const BRIGHTNESS_OPTIONS: readonly BrightnessOption[] = [
    { class: "brightness-50", label: "50%" },
    { class: "brightness-75", label: "75%" },
    { class: "brightness-90", label: "90%" },
    { class: "brightness-95", label: "95%" },
    { class: "brightness-100", label: "100%" },
    { class: "brightness-105", label: "105%" },
    { class: "brightness-110", label: "110%" },
    { class: "brightness-125", label: "125%" },
    { class: "brightness-150", label: "150%" },
    { class: "brightness-200", label: "200%" },
];

export const CONTRAST_OPTIONS = [
    { value: 0.5, class: "contrast-50", label: "50%" },
    { value: 0.75, class: "contrast-75", label: "75%" },
    { value: 1, class: "contrast-100", label: "100%" },
    { value: 1.25, class: "contrast-125", label: "125%" },
    { value: 1.5, class: "contrast-150", label: "150%" },
    { value: 2, class: "contrast-200", label: "200%" },
];

// Tailwind saturate options mapped to slider steps
export const SATURATE_OPTIONS: readonly SaturateOption[] = [
    { value: 0.5, class: "saturate-50", label: "50%" },
    { value: 1, class: "saturate-100", label: "100%" },
    { value: 1.5, class: "saturate-150", label: "150%" },
    { value: 2, class: "saturate-200", label: "200%" },
];

export const BORDER_RADIUS_OPTIONS: readonly {
    value: number;
    class: string;
    label: string;
    remValue: any;
}[] = [
    { value: 0, class: "!rounded-none", label: "0%", remValue: "0rem" },
    { value: 1, class: "!rounded-sm", label: "8%", remValue: "0.125rem" },
    { value: 2, class: "!rounded", label: "17%", remValue: "0.25rem" },
    { value: 3, class: "!rounded-md", label: "25%", remValue: "0.375rem" },
    { value: 4, class: "!rounded-lg", label: "33%", remValue: "0.5rem" },
    { value: 5, class: "!rounded-xl", label: "50%", remValue: "0.75rem" },
    { value: 6, class: "!rounded-2xl", label: "67%", remValue: "1rem" },
    { value: 7, class: "!rounded-3xl", label: "100%", remValue: "1.5rem" },
];

export const SHADOW_OPTIONS: readonly {
    value: number;
    class: string;
    label: string;
}[] = [
    { value: 0, class: "!shadow-none", label: "0%" }, // No shadow
    { value: 1, class: "!shadow-sm", label: "20%" }, // Smallest shadow
    { value: 2, class: "!shadow-md", label: "40%" }, // Medium shadow
    { value: 3, class: "!shadow-lg", label: "60%" }, // Large shadow
    { value: 4, class: "!shadow-xl", label: "80%" }, // Extra large shadow
    { value: 5, class: "!shadow-2xl", label: "100%" }, // Largest shadow
];
export const FONT_SIZE_OPTIONS: readonly {
    value: number;
    class: string;
    icon?: number;
    label: string;
    lineHeight: string;
}[] = [
    { value: 0, class: "!text-sm", icon: 14, label: "SM", lineHeight: "20px" },
    {
        value: 1,
        class: "!text-base",
        icon: 16,
        label: "MD",
        lineHeight: "24px",
    },
    { value: 2, class: "!text-lg", icon: 18, label: "LG", lineHeight: "28px" },
    {
        value: 3,
        class: "!text-xl",
        icon: 20,
        label: "Default",
        lineHeight: "28px",
    },
    {
        value: 4,
        class: "!text-2xl",
        icon: 24,
        label: "2XL",
        lineHeight: "32px",
    },
    {
        value: 5,
        class: "!text-3xl",
        icon: 30,
        label: "3XL",
        lineHeight: "36px",
    },
    {
        value: 6,
        class: "!text-4xl",
        icon: 36,
        label: "4XL",
        lineHeight: "40px",
    },
    { value: 7, class: "!text-5xl", icon: 48, label: "5XL", lineHeight: "1" },
];

export const GAP_OPTIONS: readonly {
    value: number;
    class: string;
    label: string;
    remValue: string;
}[] = [
    { value: 0, class: "gap-0", label: "0px", remValue: "0rem" },
    { value: 1, class: "gap-0.5", label: "2px", remValue: "0.125rem" },
    { value: 2, class: "gap-1", label: "4px", remValue: "0.25rem" },
    { value: 3, class: "gap-1.5", label: "6px", remValue: "0.375rem" },
    { value: 4, class: "gap-2", label: "8px", remValue: "0.5rem" },
    { value: 5, class: "gap-2.5", label: "10px", remValue: "0.625rem" },
    { value: 6, class: "gap-3", label: "12px", remValue: "0.75rem" },
    { value: 7, class: "gap-3.5", label: "14px", remValue: "0.875rem" },
    { value: 8, class: "gap-4", label: "16px", remValue: "1rem" },
    { value: 9, class: "gap-5", label: "20px", remValue: "1.25rem" },
    { value: 10, class: "gap-6", label: "24px", remValue: "1.5rem" },
    { value: 11, class: "gap-8", label: "32px", remValue: "2rem" },
    { value: 12, class: "gap-10", label: "40px", remValue: "2.5rem" },
    { value: 13, class: "gap-11", label: "44px", remValue: "2.75rem" },
    { value: 14, class: "gap-12", label: "48px", remValue: "3rem" },
];
